import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import type { QcIncidentOpenMode } from "@/lib/qc/qcIncidentNav";

export type QcIncidentModalMode = QcIncidentOpenMode;

export interface QcIncidentModalState {
  incidentId: number;
  mode: QcIncidentModalMode;
}

interface QcIncidentModalContextValue {
  modal: QcIncidentModalState | null;
  openQcIncident: (incidentId: number, mode?: QcIncidentModalMode) => void;
  closeQcIncident: () => void;
  setQcIncidentMode: (mode: QcIncidentModalMode) => void;
}

const QcIncidentModalContext = createContext<QcIncidentModalContextValue | null>(null);

export function QcIncidentModalProvider({ children }: { children: ReactNode }): JSX.Element {
  const [modal, setModal] = useState<QcIncidentModalState | null>(null);

  const openQcIncident = useCallback((incidentId: number, mode: QcIncidentModalMode = "view") => {
    if (!Number.isFinite(incidentId) || incidentId <= 0) {
      return;
    }
    setModal({ incidentId, mode });
  }, []);

  const closeQcIncident = useCallback(() => {
    setModal(null);
  }, []);

  const setQcIncidentMode = useCallback((mode: QcIncidentModalMode) => {
    setModal((current) => (current == null ? current : { ...current, mode }));
  }, []);

  const value = useMemo(
    () => ({
      modal,
      openQcIncident,
      closeQcIncident,
      setQcIncidentMode,
    }),
    [modal, openQcIncident, closeQcIncident, setQcIncidentMode],
  );

  return <QcIncidentModalContext.Provider value={value}>{children}</QcIncidentModalContext.Provider>;
}

export function useQcIncidentModal(): QcIncidentModalContextValue {
  const context = useContext(QcIncidentModalContext);
  if (context == null) {
    throw new Error("useQcIncidentModal must be used within QcIncidentModalProvider.");
  }
  return context;
}

/** Safe hook for components that may render outside the provider (falls back to no-op). */
export function useOptionalQcIncidentModal(): QcIncidentModalContextValue | null {
  return useContext(QcIncidentModalContext);
}
